import React, { useState, useEffect, useRef } from "react";
import styled, { css } from "styled-components";
import { Link, NavLink } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { getAuth } from "firebase/auth";
import { useTranslation } from "react-i18next";

// Imported for Language Selector
import { IoLanguageSharp, IoHome, IoSettingsSharp } from "react-icons/io5";

import logoD from "../assets/ASitesPlanner2.png";
import logoL from "../assets/ASitesPlanner (1).png";
import miniLogo from "../assets/asitesLogoDL.png";

// Import flags (Make sure these paths are correct in your project structure!)
import enFlag from "../assets/flags/engFlag.webp";
import alFlag from "../assets/flags/albanianFlag.png";
import frFlag from "../assets/flags/frFlag.webp";
import deFlag from "../assets/flags/grFlag.jpg";
import { GiBrainstorm } from "react-icons/gi";
import {
  FaCheckCircle,
  FaUsers,
  FaUser,
  FaBell,
  FaChartArea,
} from "react-icons/fa";
import { LuClockArrowUp } from "react-icons/lu";
import { FaMoneyBillTrendUp, FaMoon } from "react-icons/fa6";
import { IoIosSunny } from "react-icons/io";
import { GoGoal } from "react-icons/go";
import { GiStairsGoal } from "react-icons/gi";

const Sidebar = ({ theme, switchTheme, onWidthChange }) => {
  const auth = getAuth();
  const { currentUser, isEmployee } = useAuth();
  const user = auth.currentUser;
  const { t, i18n } = useTranslation();

  const [collapsed, setCollapsed] = useState(false);
  const [isNarrowScreen, setIsNarrowScreen] = useState(false);
  // NEW: State for language dropdown visibility
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  // NEW: Ref for language dropdown
  const languageDropdownRef = useRef(null);

  const NARROW_BREAKPOINT = 1700;

  // Collapse by default when screen width <= 1300px
  useEffect(() => {
    const checkWidth = () => {
      const isNarrow = window.innerWidth <= NARROW_BREAKPOINT;
      setIsNarrowScreen(isNarrow);
      setCollapsed(isNarrow);
    };
    checkWidth();
    window.addEventListener("resize", checkWidth);
    return () => window.removeEventListener("resize", checkWidth);
  }, []);

  // NEW: Effect to close language dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        languageDropdownRef.current &&
        !languageDropdownRef.current.contains(event.target)
      ) {
        setShowLanguageDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Define available languages with their data
  const languages = [
    {
      code: "en",
      name: "English",
      flag: enFlag,
      nativeName: "English",
    },
    {
      code: "al",
      name: "Albanian",
      flag: alFlag,
      nativeName: "Shqip",
    },
    {
      code: "fr",
      name: "French",
      flag: frFlag,
      nativeName: "Français",
    },
    {
      code: "de",
      name: "German",
      flag: deFlag,
      nativeName: "Deutsch",
    },
  ];

  // NEW: Language change handler
  const handleLanguageChange = (languageCode) => {
    i18n.changeLanguage(languageCode);
    setShowLanguageDropdown(false);

    // Update HTML direction for RTL languages
    if (languageCode === "ar") {
      document.documentElement.dir = "rtl";
    } else {
      document.documentElement.dir = "ltr";
    }
  };

  // Report width to parent component
  useEffect(() => {
    const contentWidth = isNarrowScreen ? 0 : collapsed ? 80 : 240;
    onWidthChange(contentWidth);
  }, [collapsed, isNarrowScreen]);

  // Handle click to close the overlay when clicking on a link
  const handleLinkClick = () => {
    if (isNarrowScreen) {
      setCollapsed(true);
    }
  };

  if (isNarrowScreen) {
    onWidthChange(80);
  } else {
    onWidthChange(collapsed ? 80 : 240);
  }

  return (
    <>
      {/* Add Overlay Backdrop */}
      {isNarrowScreen && !collapsed && (
        <OverlayBackdrop onClick={() => setCollapsed(true)} />
      )}

      <Container collapsed={collapsed} isNarrowScreen={isNarrowScreen}>
        {/* Collapse Button */}
        <CollapseButton
          onClick={() => setCollapsed((prev) => !prev)}
          isNarrowScreen={isNarrowScreen}
        >
          {collapsed ? "»" : "«"}
        </CollapseButton>

        {/* LOGO */}
        {theme === "dark" ? (
          <img className="logo" src={logoD} alt="Logo" />
        ) : (
          <img className="logo" src={logoL} alt="Logo" />
        )}

        {/* USER */}
        <MiddleItems>
          {/* User links should close the sidebar when clicked on narrow screens */}
          {!collapsed && user && (
            <Link
              to={"/account"}
              style={{ textDecoration: "none" }}
              onClick={handleLinkClick}
            >
              <div className="user link-styles">
                <FaUser className="icon" />
                {`${user.displayName}`}
              </div>
            </Link>
          )}
          {collapsed && user && (
            <Link
              to={"/account"}
              style={{ textDecoration: "none" }}
              onClick={handleLinkClick}
            >
              <div className="user link-styles">
                <FaUser className="icon" />
              </div>
            </Link>
          )}
          {/* MENU */}
          <Menu collapsed={collapsed}>
            <StyledLink
              collapsed={collapsed}
              to="/app"
              onClick={handleLinkClick}
            >
              <IoHome className="icon" /> {!collapsed && t("sidebar.home")}
            </StyledLink>

            <StyledLink
              collapsed={collapsed}
              to="/growth"
              onClick={handleLinkClick}
            >
              <GiStairsGoal className="icon" />{" "}
              {!collapsed && t("sidebar.myGrowth")}
            </StyledLink>

            <StyledLink
              collapsed={collapsed}
              to="/finances"
              onClick={handleLinkClick}
            >
              <FaMoneyBillTrendUp className="icon" />{" "}
              {!collapsed && t("sidebar.finances")}
            </StyledLink>

            <StyledLink
              collapsed={collapsed}
              to="/reminders"
              onClick={handleLinkClick}
            >
              <FaBell className="icon" /> {!collapsed && t("sidebar.reminders")}
            </StyledLink>

            <StyledLink
              collapsed={collapsed}
              to="/goals"
              onClick={handleLinkClick}
            >
              <GoGoal className="icon" /> {!collapsed && t("sidebar.goals")}
            </StyledLink>

            <StyledLink
              collapsed={collapsed}
              to="/work-hours"
              onClick={handleLinkClick}
            >
              <LuClockArrowUp className="icon" />{" "}
              {!collapsed && t("sidebar.workHours")}
            </StyledLink>

            {isEmployee && (
              <StyledLink
                collapsed={collapsed}
                to="/work-tracker"
                onClick={handleLinkClick}
              >
                <FaCheckCircle className="icon" />{" "}
                {!collapsed && t("sidebar.workTracker")}
              </StyledLink>
            )}

            <StyledLink
              collapsed={collapsed}
              to="/brain-dump"
              onClick={handleLinkClick}
            >
              <GiBrainstorm className="icon" />{" "}
              {!collapsed && t("sidebar.brainDump")}
            </StyledLink>

            <StyledLink
              collapsed={collapsed}
              to="/settings"
              onClick={handleLinkClick}
            >
              <IoSettingsSharp className="icon" />{" "}
              {!collapsed && t("sidebar.settings")}
            </StyledLink>
          </Menu>
        </MiddleItems>

        {/* NEW: 🌐 LANGUAGE SELECTOR */}
        {/* <LanguageSelectorWrapper
          ref={languageDropdownRef}
          collapsed={collapsed}
          isNarrowScreen={isNarrowScreen}
        >
          <LanguageIcon
            onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
            title={t("navbar.changeLanguage") || "Change Language"}
          >
            <IoLanguageSharp />
          </LanguageIcon>

          {showLanguageDropdown && (
            <LanguageDropdown collapsed={collapsed}>
              {languages.map((language) => (
                <LanguageOption
                  key={language.code}
                  className={i18n.language === language.code ? "active" : ""}
                  onClick={() => handleLanguageChange(language.code)}
                >
                  <img
                    src={language.flag}
                    alt={language.name}
                    className="language-flag"
                  />
                  {!collapsed && ( // Only show names if not collapsed
                    <div className="language-info">
                      <span className="language-name">{language.name}</span>
                      <span className="language-native">
                        {language.nativeName}
                      </span>
                    </div>
                  )}
                  {i18n.language === language.code && (
                    <span className="language-check">✓</span>
                  )}
                </LanguageOption>
              ))}
            </LanguageDropdown>
          )}
        </LanguageSelectorWrapper> */}
        {/* END NEW LANGUAGE SELECTOR */}

        {/* THEME SWITCH */}
        <ToggleSwitch onClick={switchTheme} isDark={theme === "dark"}>
          <IoIosSunny className="icon sun" />
          <FaMoon className="icon moon" />
          <div className="thumb" />
        </ToggleSwitch>
      </Container>
    </>
  );
};

export default Sidebar;

/* ---------------------- */
/*      STYLING BELOW     */
/* ---------------------- */

// New Overlay component for the backdrop
const OverlayBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  z-index: 99;
`;

const Container = styled.div`
  width: ${({ collapsed }) => (collapsed ? "80px" : "240px")};
  height: 100vh;
  background: var(--secondaryBackgroundColor);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: ${({ collapsed }) => (collapsed ? "1rem 0.5rem" : "2rem 0.4rem")};
  position: fixed;
  color: var(--fontPrimaryColor);
  border-radius: 0 30px 30px 0;
  transition: all 0.5s ease;
  overflow: hidden;
  z-index: 100;
  min-width: 80px;

  // Narrow screen styles (mobile sidebar open)
  ${({ collapsed, isNarrowScreen }) =>
    !collapsed &&
    isNarrowScreen &&
    css`
      width: 240px;
      box-shadow: 4px 0 20px rgba(0, 0, 0, 0.3);
    `}

  // Narrow screen styles (mobile sidebar closed/collapsed)
  ${({ collapsed, isNarrowScreen }) =>
    collapsed &&
    isNarrowScreen &&
    css`
      width: 80px;
    `}

  .logo {
    width: ${({ collapsed }) => (collapsed ? "50px" : "160px")};
    align-self: center;
    margin-top: 1em;
    transition: 0.35s ease;
  }

  .mini {
    margin-top: 1.2em;
  }

  .user {
    margin-top: 2em;
    background-color: var(--thirdBackgroundColor);
    padding: 18px 24px;
    border-radius: 20px;
    display: flex;
    align-items: center;
    gap: 8px;
    text-decoration: none;
    transition: 0.5s;
    width: ${({ collapsed }) => (collapsed ? "auto" : "200px")};

    .icon {
      font-size: 20px;
    }
  }
`;

const MiddleItems = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0;
  flex: 1;
  justify-content: center;
  margin-bottom: 30%;
`;

const CollapseButton = styled.div`
  width: 100%;
  font-size: 22px;
  color: var(--fontPrimaryColor);
  cursor: pointer;
  transition: 0.3s;
  opacity: 0.6;
  position: absolute;
  right: 0;
  top: 0;
  padding-right: ${({ isNarrowScreen }) => (isNarrowScreen ? "15px" : "10px")};
  text-align: right;

  &:hover {
    opacity: 1;
  }
`;

const Menu = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: ${({ collapsed }) => (collapsed ? "2em" : "1em")};
  transition: 0.35s ease;

  h3 {
    font-weight: 400;
    color: var(--fontSecondaryColor);
    padding-left: 1rem;
  }

  @media (max-height: 750px) {
    gap: 0px;
  }
`;

const StyledLink = styled(NavLink)`
  color: var(--fontPrimaryColor);
  text-decoration: none;
  font-size: 16px;
  transition: 0.25s;
  display: flex;
  align-items: center;
  gap: ${({ collapsed }) => (collapsed ? "0px" : "15px")};
  padding: 16px 18px;
  border-radius: 18px;
  justify-content: ${({ collapsed }) => (collapsed ? "center" : "flex-start")};

  .icon {
    font-size: 22px;
  }

  @media (max-height: 940px) {
    font-size: 14px;
  }

  &.active {
    color: #38bdf8;
  }

  &:hover {
    color: #38bdf8;
    background: rgba(56, 189, 248, 0.07);
  }
`;

// ------------------------------------
// NEW LANGUAGE SELECTOR STYLES
// ------------------------------------

const LanguageSelectorWrapper = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  justify-content: ${({ collapsed }) => (collapsed ? "center" : "flex-start")};
  padding: ${({ collapsed }) => (collapsed ? "0" : "0 18px")};
  margin-top: 1em;

  // Hide on large screens where Navbar is visible
  @media (min-width: 651px) {
    // Only show if it's a narrow screen (tablet/desktop sidebar)
    display: ${({ isNarrowScreen }) => (isNarrowScreen ? "flex" : "none")};
  }

  // Force visible on mobile screens where sidebar is the main navigation
  @media (max-width: 650px) {
    display: flex;
    // Push the language selector up slightly to be above the theme switch
    margin-bottom: 30px;
  }
`;

const LanguageIcon = styled.div`
  padding: 10px;
  color: var(--fontPrimaryColor);
  border-radius: 50%;
  cursor: pointer;
  font-size: 2.5em;
  background-color: var(--thirdBackgroundColor);
  transition: 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: var(--borderColor);
  }
`;

const LanguageDropdown = styled.div`
  position: absolute;
  bottom: 100%; // Appears above the icon
  min-width: 200px;
  margin-bottom: 10px;

  // Positioning based on collapsed state
  ${({ collapsed }) =>
    collapsed
      ? css`
          left: 100%; // To the right of the collapsed sidebar
          margin-left: 10px;
        `
      : css`
          left: 18px; // Aligns with the non-collapsed sidebar links
        `}

  background: var(--primaryBackgroundColor);
  border: 1px solid var(--borderColor);
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  overflow: hidden;
`;

const LanguageOption = styled.div`
  display: flex;
  align-items: center;
  padding: 10px 15px;
  cursor: pointer;
  transition: background-color 0.2s;
  border-bottom: 1px solid var(--borderColor);
  justify-content: space-between;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: var(--secondaryBackgroundColor);
  }

  &.active {
    background-color: var(--thirdBackgroundColor);
  }

  .language-flag {
    width: 24px;
    height: 16px;
    border-radius: 2px;
    margin-right: 12px;
    object-fit: cover;
    flex-shrink: 0;
  }

  .language-info {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    margin-right: 12px;

    .language-name {
      font-size: 0.9em;
      font-weight: 500;
      color: var(--fontPrimaryColor);
    }

    .language-native {
      font-size: 0.8em;
      color: var(--fontSecondaryColor);
    }
  }

  .language-check {
    color: #38bdf8;
    font-weight: bold;
    font-size: 1.2em;
    flex-shrink: 0;
  }
`;

// ------------------------------------
// EXISTING STYLES
// ------------------------------------

const ToggleSwitch = styled.div`
  position: absolute;
  bottom: 3em;
  left: 50%;
  transform: translateX(-50%);
  width: 62px;
  height: 30px;
  background: ${({ isDark }) => (isDark ? "#495269" : "#e8e8e8")};
  border-radius: 40px;
  padding: 4px;
  cursor: pointer;
  transition: background 0.3s ease;

  .icon {
    position: absolute;
    font-size: 22px;
    opacity: 0.45;
  }

  .sun {
    left: 5px;
  }

  .moon {
    right: 9px;
    font-size: 20px;
  }

  .thumb {
    width: 22px;
    height: 22px;
    background: ${({ isDark }) => (isDark ? "#fff" : "#111")};
    border-radius: 50%;
    position: absolute;
    transform: ${({ isDark }) =>
      isDark ? "translateX(32px)" : "translateX(0)"};
    transition: transform 0.28s cubic-bezier(0.4, 0, 0.2, 1),
      background 0.3s ease;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
  }
`;
